import { useCallback } from 'react';
import { Direction_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { useQueryClient } from 'react-query';
import { getGenerateSummaryQueryKey } from '../queries';
import { GenerateSummaryIO } from '../types';

type UpdateGenerateSummaryParams = {
  mediaId: string;
  locationId: string;
  faxId: string;
  direction: Direction_Enum;
};

type UpdateGenerateSummaryData = GenerateSummaryIO['output'];

/**
 * A hook that returns utility functions to update the generate summary query cache.
 * This is useful for optimistic updates or updating cache after mutations.
 *
 */
export const useUpdateGenerateSummaryCache = () => {
  const queryClient = useQueryClient();

  /**
   * Updates the generate summary cache for a specific fax
   * @param params - The parameters used to identify the query (mediaId, locationId, faxId)
   * @param updater - Either new data or a function that receives old data and returns new data
   */
  const updateGenerateSummary = useCallback(
    (
      params: UpdateGenerateSummaryParams,
      updater:
        | UpdateGenerateSummaryData
        | ((oldData?: UpdateGenerateSummaryData) => UpdateGenerateSummaryData | undefined)
    ) => {
      const queryKey = getGenerateSummaryQueryKey(params);

      queryClient.setQueryData<UpdateGenerateSummaryData | undefined>(queryKey, (oldData) => {
        if (typeof updater === 'function') {
          return updater(oldData);
        }
        return updater;
      });
    },
    [queryClient]
  );

  return updateGenerateSummary;
};
